import api from './api';

/**
 * Session service for API calls
 */
const sessionService = {
  /**
   * Get sessions for an event
   */
  async getEventSessions(eventId, params = {}) {
    const response = await api.get(`/events/${eventId}/sessions`, { params });
    return response.data;
  },

  /**
   * Get session by ID
   */
  async getSessionById(id) {
    const response = await api.get(`/sessions/${id}`);
    return response.data;
  },

  /**
   * Create session
   */
  async createSession(eventId, sessionData) {
    const response = await api.post(`/events/${eventId}/sessions`, sessionData);
    return response.data;
  },

  /**
   * Update session
   */
  async updateSession(id, sessionData) {
    const response = await api.put(`/sessions/${id}`, sessionData);
    return response.data;
  },

  /**
   * Delete session
   */
  async deleteSession(id) {
    const response = await api.delete(`/sessions/${id}`);
    return response.data;
  },

  /**
   * Publish session
   */
  async publishSession(id) {
    const response = await api.post(`/sessions/${id}/publish`);
    return response.data;
  },

  /**
   * Bulk publish sessions
   */
  async bulkPublishSessions(eventId, sessionIds) {
    const response = await api.post(`/events/${eventId}/sessions/bulk-publish`, { sessionIds });
    return response.data;
  },

  /**
   * Bulk delete sessions
   */
  async bulkDeleteSessions(eventId, sessionIds) {
    const response = await api.post(`/events/${eventId}/sessions/bulk-delete`, { sessionIds });
    return response.data;
  },

  /**
   * Bulk update session tags (add, remove, or set)
   */
  async bulkTagSessions(eventId, sessionIds, action, tagIds) {
    const response = await api.post(`/events/${eventId}/sessions/bulk-tags`, { sessionIds, action, tagIds });
    return response.data;
  },

  /**
   * Get speakers for a session
   */
  async getSpeakers(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/speakers`);
    return response.data;
  },

  /**
   * Add speaker to session
   */
  async addSpeaker(sessionId, speakerData) {
    const response = await api.post(`/sessions/${sessionId}/speakers`, speakerData);
    return response.data;
  },

  /**
   * Update speaker
   */
  async updateSpeaker(sessionId, speakerId, speakerData) {
    const response = await api.put(`/sessions/${sessionId}/speakers/${speakerId}`, speakerData);
    return response.data;
  },

  /**
   * Remove speaker
   */
  async removeSpeaker(sessionId, speakerId) {
    const response = await api.delete(`/sessions/${sessionId}/speakers/${speakerId}`);
    return response.data;
  },

  /**
   * Add session to schedule
   * @param {number} sessionId - The session ID to add
   */
  async addToSchedule(sessionId) {
    const response = await api.post(`/sessions/${sessionId}/schedule`);
    return response.data;
  },

  /**
   * Remove session from schedule
   */
  async removeFromSchedule(sessionId) {
    const response = await api.delete(`/sessions/${sessionId}/schedule`);
    return response.data;
  },

  /**
   * Get user's schedule for event
   */
  async getUserSchedule(eventId) {
    const response = await api.get(`/events/${eventId}/schedule`);
    return response.data;
  },

  /**
   * Export schedule as iCal
   */
  async exportIcal(eventId) {
    const response = await api.get(`/events/${eventId}/schedule/ical`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'schedule.ics');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get an attendee's schedule (organizer view)
   */
  async getAttendeeSchedule(eventId, userId) {
    const response = await api.get(`/organizer/events/${eventId}/attendees/${userId}/schedule`);
    return response.data;
  },

  /**
   * Add a session to an attendee's schedule (organizer action)
   */
  async addSessionToAttendeeSchedule(eventId, userId, sessionId) {
    const response = await api.post(`/organizer/events/${eventId}/attendees/${userId}/schedule`, { sessionId });
    return response.data;
  },

  /**
   * Remove a session from an attendee's schedule (organizer action)
   */
  async removeSessionFromAttendeeSchedule(eventId, userId, sessionId) {
    const response = await api.delete(`/organizer/events/${eventId}/attendees/${userId}/schedule/${sessionId}`);
    return response.data;
  },

  /**
   * Get session attendees
   */
  async getAttendees(sessionId, params = {}) {
    const response = await api.get(`/sessions/${sessionId}/attendees`, { params });
    return response.data;
  },

  /**
   * Check in attendee
   */
  async checkInAttendee(sessionId, userId) {
    const response = await api.post(`/sessions/${sessionId}/attendees/${userId}/check-in`);
    return response.data;
  },

  /**
   * Search users with the presenter role by name
   */
  async searchPresenters(search = '') {
    const response = await api.get('/users/presenters', { params: { search } });
    return response.data;
  },

  /**
   * Get presenters for an event
   */
  async getPresenters(eventId, params = {}) {
    const response = await api.get(`/events/${eventId}/sessions/presenters`, { params });
    return response.data;
  },

  /**
   * Get tracks for an event
   */
  async getTracks(eventId) {
    const response = await api.get(`/events/${eventId}/sessions/tracks`);
    return response.data;
  },

  /**
   * Get schedule dates for an event
   */
  async getScheduleDates(eventId) {
    const response = await api.get(`/events/${eventId}/sessions/dates`);
    return response.data;
  },

  /**
   * Get sessions grouped by room for an event
   */
  async getSessionsByRoom(eventId) {
    const response = await api.get(`/events/${eventId}/sessions/by-room`);
    return response.data;
  },

  /**
   * Check for room time conflicts
   * @param {number} eventId
   * @param {number} roomId
   * @param {string} startTime - ISO timestamp
   * @param {string} endTime - ISO timestamp
   * @param {number} [excludeSessionId] - Session ID to exclude from conflict check
   */
  async checkRoomConflicts(eventId, roomId, startTime, endTime, excludeSessionId) {
    const params = { roomId, startTime, endTime };
    if (excludeSessionId) params.excludeSessionId = excludeSessionId;
    const response = await api.get(`/events/${eventId}/sessions/room-conflicts`, { params });
    return response.data;
  },

  /**
   * Batch assign rooms to sessions
   * @param {number} eventId
   * @param {Array<{sessionId: number, roomId: number|null}>} assignments
   */
  async batchAssignRooms(eventId, assignments) {
    const response = await api.put(`/events/${eventId}/sessions/batch-assign-rooms`, { assignments });
    return response.data;
  },
};

export default sessionService;
